import React from "react";
import {
  Grid,
  Box,
  Typography,
  Pagination,
  Autocomplete,
  TextField,
  ListSubheader,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseIcon from "@mui/icons-material/Close";

const cardStyle = {
  backgroundColor: "#FFFFFF",
  border: "1px solid #E2E8F0",
  borderRadius: "8px",
  padding: "10px 14px",
  width: "100%",
  minHeight: "56px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
};

export default function SheetsStepMapping({
  data = [],
  setData,
  variablesByCategory = {},
  page,
  setPage,
  setIsValid,
}) {
  React.useEffect(() => {
    setIsValid(true);
  }, [setIsValid]);

  const rowsPerPage = 10;
  const startIndex = page * rowsPerPage;
  const currentItems = data.slice(startIndex, startIndex + rowsPerPage);

  const flatOptions = React.useMemo(() => {
    return Object.entries(variablesByCategory || {}).flatMap(
      ([category, variables]) =>
        variables.map((v) => ({
          label: v,
          category,
        }))
    );
  }, [variablesByCategory]);

  function isVariableDisabled(variable, currentIndex) {
    if (variable === "WhatsApp" || variable === "Email") return false;

    return data.some(
      (item, idx) => idx !== currentIndex && item.variable === variable
    );
  }

  function handleSelectChange(index, value) {
    setData((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, variable: value || null } : item
      )
    );
  }

  const allOptions = React.useMemo(
    () => [
      { label: "Não importar", category: "Outros" },
      ...flatOptions,
    ],
    [flatOptions]
  );

  return (
    <Box sx={{ width: "100%", mt: 1 }}>
      <Grid container spacing={2.5}>
        {currentItems.map((item, localIndex) => {
          const absoluteIndex = startIndex + localIndex;
          if (!item?.field) return null;

          const currentValue =
            allOptions.find((v) => v.label === item.variable) ||
            allOptions.find((v) => v.label === "Não importar");

          const isNaoImportar =
            !item.variable || item.variable === "Não importar";

          return (
            <Grid container item xs={12} key={absoluteIndex} alignItems="center">
              {/* COLUNA ORIGINAL */}
              <Grid item xs={5.5}>
                <Box sx={cardStyle}>
                  <Typography
                    sx={{
                      fontSize: "11px",
                      fontWeight: 700,
                      color: "#64748B",
                      textTransform: "uppercase",
                      mb: 0.5,
                    }}
                  >
                    {item.field}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#1E293B",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.value || "---"}
                  </Typography>
                </Box>
              </Grid>

              {/* SETA CENTRAL */}
              <Grid item xs={1} sx={{ display: "flex", justifyContent: "center" }}>
                <ArrowForwardIcon sx={{ color: "#94A3B8", fontSize: "20px" }} />
              </Grid>

              {/* AUTOCOMPLETE */}
              <Grid item xs={5.5}>
                <Autocomplete
                  options={allOptions}
                  groupBy={(option) => option.category}
                  getOptionLabel={(option) => option.label}
                  value={currentValue}
                  disableClearable={isNaoImportar}
                  clearIcon={
                    <CloseIcon
                      sx={{
                        fontSize: 18,
                        color: "#64748B",
                      }}
                    />
                  }
                  popupIcon={
                    <ArrowDropDownIcon
                      sx={{
                        fontSize: 20,
                        color: "#64748B",
                      }}
                    />
                  }
                  onChange={(e, newValue) =>
                    handleSelectChange(
                      absoluteIndex,
                      newValue?.label === "Não importar"
                        ? null
                        : newValue?.label
                    )
                  }
                  isOptionEqualToValue={(opt, val) =>
                    opt.label === val.label
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Digite para buscar ou selecione uma variável"
                      sx={{
                        "& .MuiInputBase-input": {
                          color: isNaoImportar
                            ? "#94A3B8"
                            : "#1E293B",
                        },
                      }}
                    />
                  )}

                  /* 🔥 REMOVE GLITCH DE SCROLL */
                  ListboxProps={{
                    sx: {
                      paddingTop: 0,
                    },
                  }}

                  PaperProps={{
                    sx: {
                      marginTop: 0,
                      borderRadius: "8px",
                      overflow: "hidden",
                      backgroundColor: "#FFFFFF",
                    },
                  }}

                  renderGroup={(params) => (
                    <li key={params.key}>
                      <ListSubheader
                        sx={{
                          position: "sticky",
                          top: 0,
                          zIndex: 3,
                          backgroundColor: "#ffffffff",
                          fontWeight: 700,
                          fontSize: "12px",
                          color: "#64748B",
                          borderBottom: "1px solid #E2E8F0",
                        }}
                      >
                        {params.group}
                      </ListSubheader>
                      {params.children}
                    </li>
                  )}

                  renderOption={(props, option) => {
                    const disabled =
                      option.label !== "Não importar" &&
                      isVariableDisabled(option.label, absoluteIndex);

                    return (
                      <li
                        {...props}
                        style={{
                          opacity: disabled ? 0.5 : 1,
                          pointerEvents: disabled ? "none" : "auto",
                          color:
                            option.label === "Não importar"
                              ? "#94A3B8"
                              : "#1E293B",
                        }}
                      >
                        {option.label}
                      </li>
                    );
                  }}

                  sx={{
                    "& .MuiInputBase-root": {
                      height: "56px",
                      borderRadius: "8px",
                      backgroundColor: "#FFF",
                    },
                    "& .MuiAutocomplete-clearIndicator": {
                      padding: 0,
                      background: "transparent",
                    },
                    "& .MuiAutocomplete-clearIndicator:hover": {
                      background: "transparent",
                    },
                    "& .MuiAutocomplete-popupIndicator": {
                      padding: 0,
                      background: "transparent",
                    },
                    "& .MuiAutocomplete-popupIndicator:hover": {
                      background: "transparent",
                    },
                  }}
                />
              </Grid>
            </Grid>
          );
        })}
      </Grid>

      {data.length > rowsPerPage && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Pagination
            count={Math.ceil(data.length / rowsPerPage)}
            page={page + 1}
            onChange={(e, p) => setPage(p - 1)}
            size="small"
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
}